import React, { useMemo } from "react";
import moment from "moment-timezone";
import Loader from "./Loader";
import TeamBadge from "./TeamBadge";
import { getHandoverLook, getStatusColor } from "../utils/badgeColors";
import { formatDateTimeDisplay } from "../utils/common";


/* The shared formatter, so a task's history cannot disagree with the Start and
   End times shown right above it on the same screen. This used to branch on the
   hostname; see formatDateTimeDisplay in utils/common for why that existed and
   why it is gone. */
const displayTime = (value) => formatDateTimeDisplay(value);

/**
 * Everything that has happened to one task, newest first.
 *
 * Two kinds of event share the line. Hand-overs say where the work went and
 * who sent it; status changes say what was happening to it while it sat
 * there. Reading them together is the only way to answer "why has this been
 * open for three weeks" — the answer is usually two returns and a wait.
 *
 * Props:
 *   events  : rows from /team/timeline
 *   status  : "idle" | "loading" | "succeeded" | "failed"
 */
const TaskTimeline = ({ events, status }) => {
  const rows = useMemo(() => {
    const list = Array.isArray(events) ? events : events?.data || [];
    return [...list].sort(
      (a, b) => new Date(b.EventTime) - new Date(a.EventTime),
    );
  }, [events]);

  if (status === "loading") {
    /* inline, and that word is doing real work.
     *
     * A bare <Loader /> renders .container-loader, which is
     * `position: fixed; inset: 0` — a full-VIEWPORT skeleton, whatever it is
     * nested inside. This timeline lives in the Task Details dialog, so
     * opening a task blanked the entire application for as long as the
     * timeline took to load, and the dialog then appeared on top of it. It
     * read as the whole page reloading to look at one task.
     *
     * inline renders .sk-inline instead, which is an ordinary block and stays
     * where it is put. Three rows, because that is roughly what a timeline
     * turns out to be and a skeleton should be the shape of its answer. */
    return (
      <div className="py-2">
        <Loader isLoading inline variant="table" rows={3} cols={3} />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="text-muted small py-2">
        No history recorded for this task yet.
      </div>
    );
  }

  return (
    <div className="timeline-container ms-2 mt-2 pb-2">
      {rows.map((e, i) => {
        const isHandover = e.EventType === "Handover";
        const look = getHandoverLook(e.EventName);
        const color = isHandover ? look.text : getStatusColor(e.EventName);
        const isLast = i === rows.length - 1;

        return (
          <div
            key={`${e.EventType}-${e.EventTime}-${i}`}
            className="timeline-item position-relative mb-4"
            style={{ paddingLeft: "2rem" }}
          >
            {!isLast && (
              <div
                style={{
                  position: "absolute",
                  left: "9px",
                  top: "16px",
                  bottom: "-24px",
                  width: "2px",
                  backgroundColor: color,
                  zIndex: 1,
                }}
              />
            )}

            {/* A hand-over is drawn as an open ring so it reads as a
                different kind of event from a status change at a glance,
                without relying on colour alone. */}
            <div
              className="timeline-dot position-absolute rounded-circle shadow-sm"
              style={{
                width: "14px",
                height: "14px",
                left: "3px",
                top: "4px",
                border: isHandover ? `3px solid ${color}` : "2px solid #fff",
                backgroundColor: isHandover ? "#fff" : color,
                zIndex: 2,
              }}
            />

            <div className="d-flex justify-content-between align-items-start mb-1 gap-2">
              <span
                className="fw-bold text-dark d-flex align-items-center gap-1 flex-wrap"
                style={{ fontSize: "0.85rem" }}
              >
                {isHandover ? (
                  <>
                    <i className={`bi ${look.icon}`} style={{ color }} />
                    <span style={{ color }}>{look.label}</span>
                    {e.FromTeamName && (
                      <>
                        <TeamBadge name={e.FromTeamName} />
                        <i className="bi bi-arrow-right text-muted" />
                      </>
                    )}
                    <TeamBadge name={e.ToTeamName} />
                  </>
                ) : (
                  <>
                    Changed to:{" "}
                    <span style={{ color }}>{e.EventName}</span>
                  </>
                )}
              </span>

              <span
                className="text-muted text-nowrap"
                style={{ fontSize: "0.75rem" }}
              >
                {displayTime(e.EventTime)}
              </span>
            </div>

            <div className="text-muted mb-1" style={{ fontSize: "0.8rem" }}>
              By: <span className="fw-medium text-dark">{e.ByUserName || "-"}</span>
              {isHandover && e.ToUserName && (
                <>
                  {" · to "}
                  <span className="fw-medium text-dark">{e.ToUserName}</span>
                </>
              )}
              {/* Only meaningful on a hand-over: a task left in the pool has
                  no owner yet, which is a fact worth showing rather than a
                  blank. */}
              {isHandover && !e.ToUserName && (
                <>
                  {" · "}
                  <span className="fst-italic">left for anyone in the team</span>
                </>
              )}
            </div>

            {e.Remarks && (
              <div
                className="bg-light p-2 mt-2 rounded text-muted"
                style={{
                  fontSize: "0.8rem",
                  borderLeft: `3px solid ${color}`,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {e.Remarks}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TaskTimeline;
